import React, { useState } from 'react';
import Modal from './Modal';
import { sharesApi } from '../utils/api';
import {
  Share2, Clock, Lock, Download, Link, Copy, Check, AlertCircle
} from 'lucide-react';

const ShareModal = ({ isOpen, onClose, file }) => {
  const [duration, setDuration] = useState('1d');
  const [password, setPassword] = useState('');
  const [allowDownload, setAllowDownload] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const durationOptions = [
    { value: '30m', label: '30 min' },
    { value: '1h', label: '1 hour' },
    { value: '12h', label: '12 hours' },
    { value: '1d', label: '1 day' },
    { value: '1w', label: '1 week' }
  ];

  const handleShare = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await sharesApi.create({
        fileId: file.id,
        duration,
        password: password || null,
        allowDownload
      });

      // Generate URL using current domain
      const shareUrl = `${window.location.origin}/share/${response.data.shareId}`;
      setShareUrl(shareUrl);
    } catch (err) {
      console.error('Share error:', err);
      setError('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy error:', err);
    }
  };

  const handleClose = () => {
    setDuration('1d');
    setPassword('');
    setAllowDownload(true);
    setShareUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Share file">
      {!shareUrl ? (
        <div className="space-y-6">
          {/* File info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Share2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {file?.original_name}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create a public share link
              </p>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Clock className="w-4 h-4" />
              Expiration
            </label>
            <div className="grid grid-cols-5 gap-2">
              {durationOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDuration(opt.value)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    duration === opt.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Lock className="w-4 h-4" />
              Password protection (optional)
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave empty for no password"
              className="input"
            />
          </div>

          {/* Allow download */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Download className="w-5 h-5 text-gray-500" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  Allow download
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Viewers can download the file
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allowDownload}
                onChange={(e) => setAllowDownload(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button onClick={handleClose} className="btn btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleShare}
              disabled={loading}
              className="btn btn-primary flex items-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Link className="w-5 h-5" />
              )}
              Create link
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Success */}
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Share link created!
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The link is valid until the expiration time
            </p>
          </div>

          {/* Share URL */}
          <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="input flex-1 font-mono text-sm"
              />
              <button
                onClick={handleCopy}
                className="btn btn-primary flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Settings summary */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
              <span>Expiration:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {durationOptions.find(d => d.value === duration)?.label}
              </span>
            </div>
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
              <span>Password protection:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {password ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex items-center justify-between text-gray-600 dark:text-gray-400">
              <span>Download:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {allowDownload ? 'Allowed' : 'Disabled'}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            <button onClick={handleClose} className="btn btn-primary">
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ShareModal;
